import React, { useState } from 'react';
import { Moon, Brain, Zap, Clock } from 'lucide-react';
import { SleepBreakdown } from '../sleep/SleepBreakdown';
import { SleepAnalysisSummary } from '../sleep/SleepAnalysisSummary';
import { HealthMetrics } from '../../types/health';
import { SleepMetricCard } from '../sleep/SleepMetricCard';
import { SleepCoach } from '../sleep/SleepCoach';
import { SleepKnowledge } from '../sleep/SleepKnowledge';

interface SleepTabProps {
  metrics: HealthMetrics;
  onClose: () => void;
}

export function SleepTab({ metrics, onClose }: SleepTabProps) {
  const [showSleepPlanner, setShowSleepPlanner] = useState(false);

  return (
    <div className="space-y-6">
      {/* Sleep Analysis Summary */}
      <SleepAnalysisSummary
        sleepScore={metrics.sleep.score}
        previousScore={metrics.sleep.score - 5}
        insights={[]}
      />

      {/* Sleep Metrics Grid */}
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
        <SleepMetricCard
          title="Total Sleep"
          value={metrics.sleep.hours}
          unit="hours"
          icon={<Moon className="w-4 h-4" />}
          status="success"
          comparison="+30min vs. baseline"
        />
        <SleepMetricCard
          title="Sleep Efficiency"
          value={92}
          unit="%"
          icon={<Brain className="w-4 h-4" />}
          status="success"
          comparison="Optimal range"
        />
        <SleepMetricCard
          title="Deep Sleep"
          value="1:38"
          unit="hours"
          icon={<Zap className="w-4 h-4" />}
          status="success"
          comparison="+15min vs. baseline"
        />
        <SleepMetricCard
          title="REM Sleep"
          value="1:48"
          unit="hours"
          icon={<Brain className="w-4 h-4" />}
          status="warning"
          comparison="-10min vs. baseline"
        />
      </div>

      {/* Plan My Sleep Button */}
      <button
        onClick={() => setShowSleepPlanner(true)}
        className="w-full theme-button py-3 rounded-lg flex items-center justify-center gap-2 relative group overflow-hidden"
      >
        <div className="absolute inset-0 bg-gradient-to-r from-primary via-purple-500 to-primary opacity-0 group-hover:opacity-20 transition-opacity" />
        <Clock className="w-5 h-5" />
        <span className="relative">Plan My Sleep</span>
      </button>

      {/* Sleep Breakdown */}
      <SleepBreakdown 
        sleepStages={{
          rem: 108,    // 1h 48m
          deep: 98,    // 1h 38m
          light: 224,  // 3h 44m
          awake: 20    // 20m
        }}
      />

      {/* Knowledge Center */}
      <SleepKnowledge />

      {/* Sleep Coach Modal */}
      {showSleepPlanner && (
        <SleepCoach
          onClose={() => setShowSleepPlanner(false)}
          currentSleepScore={metrics.sleep.score}
        />
      )}
    </div>
  );
}